import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { View, TouchableOpacity, ScrollView, Image, TextInput } from 'react-native';
import Checkbox from 'src/components/Checkbox/Checkbox';
import Typography from 'src/components/Text/Text';
import Divider from 'src/components/Divider/Divider';
import expandIcon from 'src/assets/expander-3x.png';
import { CheckListModalList, Selection } from '@typeRoots/common/checkListModal';
import { styles } from './CheckListSelector.styles';
import { fuzzyCheck, normalizeText } from 'src/helpers/searchHelpers';

interface CheckListSelectorProps {
  initialSelection: Selection[];
  onSelectionChange: (newSelection: Selection[]) => void;
  list: CheckListModalList;
  parentTestID?: string;
  shouldResetSearch?: boolean;
  hasSearch?: boolean;
  searchPlaceholder?: string;
}

const CheckListSelector: React.FC<CheckListSelectorProps> = ({
  initialSelection,
  onSelectionChange,
  list,
  parentTestID,
  shouldResetSearch,
  hasSearch,
  searchPlaceholder = 'Search commodities',
}) => {
  const [selection, setSelection] = useState<Selection[]>(initialSelection);
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());
  const [searchText, setSearchText] = useState<string>('');
  const searchInputRef = useRef<TextInput>(null);
  const checkListModalList: CheckListModalList = list;

  // Sync selection state when initialSelection prop changes
  useEffect(() => {
    setSelection(initialSelection);
  }, [initialSelection]);

  useEffect(() => {
    if (shouldResetSearch) {
      setSearchText('');
    }
  }, [shouldResetSearch]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchInputRef.current) {
        searchInputRef.current.focus();
      }
    }, 100);
    return () => clearTimeout(timer);
  }, []);

  const filteredList = useMemo(() => {
    if (!searchText.trim()) {
      return checkListModalList;
    }

    const normalizedSearch = normalizeText(searchText.trim());
    const exactMatches: CheckListModalList = {};
    const partialMatches: CheckListModalList = {};
    const fuzzyMatches: Record<string, Array<{ name: string; value: string }>> = {};

    Object.entries(checkListModalList).forEach(([category, subcategories]) => {
      const normCategory = normalizeText(category);

      const exactSubs: typeof subcategories = [];
      const partialSubs: typeof subcategories = [];
      const fuzzySubs: Array<{ name: string; value: string; _score: number }> = [];

      // Category-level match handling
      const catMatch = fuzzyCheck(normCategory, normalizedSearch);
      const isCatExact = normCategory === normalizedSearch;
      const isCatPartial = !isCatExact && normCategory.includes(normalizedSearch);
      const isCatFuzzy = !isCatExact && !isCatPartial && catMatch.matched;

      subcategories.forEach((sub) => {
        const normSub = normalizeText(sub.name);
        const subCheck = fuzzyCheck(normSub, normalizedSearch);

        if (normSub === normalizedSearch || isCatExact) {
          // exact sub or exact category => show all (if exact category) or exact sub(s)
          exactSubs.push(sub);
        } else if (normSub.includes(normalizedSearch) || isCatPartial) {
          partialSubs.push(sub);
        } else if (subCheck.matched || isCatFuzzy) {
          // push with score for sorting later
          const score = isCatFuzzy ? 2 : subCheck.distance; // small constant so cat-fuzzy groups together
          fuzzySubs.push({ ...sub, _score: score });
        }
      });

      if (exactSubs.length > 0) {
        // If category name exactly matches, include all children
        exactMatches[category] = isCatExact ? subcategories : exactSubs;
      }
      if (partialSubs.length > 0) {
        partialMatches[category] = partialSubs;
      }
      if (fuzzySubs.length > 0) {
        // sort fuzzy by score ascending (closest first), then name
        fuzzySubs.sort((a, b) => a._score - b._score || a.name.localeCompare(b.name));
        fuzzyMatches[category] = fuzzySubs.map((rest) => {
          const { _score, ...sub } = rest;
          console.log(_score);
          return sub;
        });
      }
    });

    // Merge tiers: exact, then partial, then fuzzy (keeping items under parents)
    // Also dedupe in case something sneaks into multiple tiers
    const merged: CheckListModalList = {};
    const add = (src: CheckListModalList) => {
      Object.entries(src).forEach(([cat, subs]) => {
        const existing = merged[cat] ?? [];
        const seen = new Set(existing.map((s) => s.value));
        const addSubs = subs.filter((s) => !seen.has(s.value));
        merged[cat] = [...existing, ...addSubs];
      });
    };

    add(exactMatches);
    add(partialMatches);
    add(fuzzyMatches as unknown as CheckListModalList);

    return merged;
  }, [checkListModalList, searchText]);

  useEffect(() => {
    if (searchText.trim()) {
      const categoriesToExpand = Object.keys(filteredList);
      setExpandedCategories(new Set(categoriesToExpand));
    }
  }, [searchText, filteredList]);

  // Handle checkbox state changes and notify parent
  const handleCheckboxChange = useCallback(
    (value: string, name: string, category: string, checked: boolean) => {
      const newSelection = checked
        ? [...selection, { type: category, subtype: { name, value } }]
        : selection.filter((item) => !(item.type === category && item.subtype.value === value));

      setSelection(newSelection); // Update local state
      onSelectionChange(newSelection); // Notify parent of the change
    },
    [selection, onSelectionChange],
  );

  // Toggle the category expansion state
  const toggleCategory = useCallback((category: string) => {
    setExpandedCategories((prev) => {
      const updated = new Set(prev);
      if (updated.has(category)) {
        updated.delete(category);
      } else {
        updated.add(category);
      }
      return updated;
    });
  }, []);

  const handleSearchChange = useCallback((text: string) => {
    setSearchText(text);
  }, []);

  const clearSearch = useCallback(() => {
    setSearchText('');
    if (searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, []);

  useEffect(() => {
    const hasQuery = !!searchText.trim();
    if (hasQuery) {
      // open only categories that are in the filtered list (i.e., relevant)
      const categoriesToExpand = Object.keys(filteredList);
      setExpandedCategories(new Set(categoriesToExpand));
    } else {
      // no query -> collapse all (or restore a default if you want)
      setExpandedCategories(new Set());
    }
  }, [searchText, filteredList]);

  const inputStyles = [
    styles.searchInput,
    {
      outline: 'none' as const,
    },
  ];

  return (
    <ScrollView
      showsVerticalScrollIndicator={false}
      bounces={false}
      contentContainerStyle={styles.container}>
      {hasSearch && (
        <View style={styles.searchContainer}>
          <View style={styles.searchInputContainer}>
            <TextInput
              ref={searchInputRef}
              style={[inputStyles, styles.input]}
              value={searchText}
              onChangeText={handleSearchChange}
              placeholder={searchPlaceholder}
              placeholderTextColor="#9CA3AF"
              editable={true}
            />
            {searchText.length > 0 && (
              <TouchableOpacity onPress={clearSearch} style={styles.clearButton}>
                <Typography variant="body1" style={styles.clearButtonText}>
                  x
                </Typography>
              </TouchableOpacity>
            )}
          </View>
        </View>
      )}

      {Object.keys(filteredList).length === 0 && searchText.trim() ? (
        <View style={styles.noResultsContainer}>
          <Typography variant="body1">
            {`No matches for '${searchText}'. Please try a different word`}
          </Typography>
        </View>
      ) : (
        Object.entries(filteredList).map(([category, subcategories]) => (
          <View key={category}>
            {subcategories.length > 1 && (
              <>
                <TouchableOpacity
                  style={styles.categoryTitle}
                  onPress={() => toggleCategory(category)}
                  testID={`${parentTestID}-category-${category}`}>
                  <Typography variant="title1">{category}</Typography>
                  <View style={styles.expandIconContainer}>
                    {expandedCategories.has(category) && (
                      <Image source={expandIcon} style={styles.expandIcon} />
                    )}
                  </View>
                </TouchableOpacity>
                <Divider />
              </>
            )}

            {/* Always render children under the category, even if there's only one */}
            {expandedCategories.has(category) && subcategories.length > 1 ? (
              subcategories.map((subcategory, index) => (
                <View
                  key={`${category}-${subcategory.value}-${index}`}
                  style={styles.subcategoryContainer}>
                  <View style={styles.checkboxContainer}>
                    <Typography variant="title2">{subcategory.name}</Typography>
                    <Checkbox
                      testID={`${parentTestID}-subcategory-${subcategory.name}`}
                      active={selection.some(
                        (item) =>
                          item.type === category && item.subtype.value === subcategory.value,
                      )}
                      onChange={(checked) =>
                        handleCheckboxChange(subcategory.value, subcategory.name, category, checked)
                      }
                    />
                  </View>
                  <Divider />
                </View>
              ))
            ) : subcategories.length === 1 ? (
              <>
                <View style={styles.checkboxContainer}>
                  <Typography variant="title1">{subcategories[0].name}</Typography>
                  <Checkbox
                    testID={`${parentTestID}-subcategory-${subcategories[0].value}`}
                    active={selection.some(
                      (item) =>
                        item.type === category && item.subtype.value === subcategories[0].value,
                    )}
                    onChange={(checked) =>
                      handleCheckboxChange(
                        subcategories[0].value,
                        subcategories[0].name,
                        category,
                        checked,
                      )
                    }
                  />
                </View>
                <Divider />
              </>
            ) : null}
          </View>
        ))
      )}
    </ScrollView>
  );
};

export default CheckListSelector;
